import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Switch } from "@/components/ui/switch";

import { useVendorsApi } from "../../../../common/hooks/use-vendors-api.hook";

/** Whether a vendor config has native AR files on. */
export const arPublicFilesOn = (config: any): boolean =>
    config?.integration?.features?.ar?.settings?.publicFiles === true;

interface Props {
    /** The vendor's uuid (what its config is keyed by); its _id is accepted too. */
    vendorId: string;
    config: any;
}

/**
 * Native AR files for one vendor: public, plain AR copies of models the catalogue
 * otherwise keeps encrypted. Only a super admin turns them on, through their own route
 * (the API keeps the stored value on every config save, from either side).
 *
 * Applied at once, and kept out of the `vendor` object on purpose: the page's Save
 * sends a diff of `config`, and an `integration` changed here would ride along.
 */
export const VendorArPublicFiles = ({ vendorId, config }: Props) => {
    const { t } = useTranslation();
    const vendorsAPI = useVendorsApi();
    const [enabled, setEnabled] = useState(arPublicFilesOn(config));
    const [saving, setSaving] = useState(false);

    const onChange = async (next: boolean) => {
        setSaving(true);
        setEnabled(next);
        try {
            await vendorsAPI.setArPublicFiles(vendorId, next);
            toast.success(t(next ? "ADMIN_VENDOREDIT.BRANDING.AR_PUBLIC_FILES_ON" : "ADMIN_VENDOREDIT.BRANDING.AR_PUBLIC_FILES_OFF"));
        } catch (error: any) {
            setEnabled(!next);
            // The API's own reason, as the rest of the vendor page shows it.
            const serverMessage = error?.message && !String(error.message).startsWith("Request failed")
                ? `: ${error.message}`
                : "";
            const failed = t("ADMIN_VENDOREDIT.BRANDING.AR_PUBLIC_FILES_FAILED");
            toast.error(serverMessage ? failed.replace(/\.\s*$/, "") + serverMessage : failed);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex items-start justify-between gap-4 rounded-lg border p-4 sm:col-span-2 lg:col-span-8">
            <div className="flex flex-col gap-1">
                <label htmlFor="vendor-config-ar-public-files" className="text-sm font-medium">
                    {t("ADMIN_VENDOREDIT.BRANDING.AR_PUBLIC_FILES_TITLE")}
                </label>
                <span className="text-xs text-muted-foreground">
                    {t("ADMIN_VENDOREDIT.BRANDING.AR_PUBLIC_FILES_DESC")}
                </span>
            </div>
            <Switch
                id="vendor-config-ar-public-files"
                checked={enabled}
                disabled={saving}
                onCheckedChange={onChange}
            />
        </div>
    );
};
